import { twJoin } from "tailwind-merge";

import type { ChatReplyDto } from "@/api/types";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

interface SystemChatMessageProps {
  chatMessage: ChatReplyDto;
  chatName?: string;
  presentation?: "bubble" | "preview";
}

export function SystemChatMessage({ chatMessage, chatName, presentation }: SystemChatMessageProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  function getSystemMessage(): string {
    let systemMessage = "";
    switch (chatMessage.replyType) {
      case "systemCreation":
        systemMessage =
          chatMessage.author?.id === sessionUser.id
            ? t("page.chat-detail.chat-created.self", { group: chatName || "" })
            : t("page.chat-detail.chat-created.other.not-affected", {
                author: chatMessage.author?.fullName || "",
                group: chatName || "",
              });
        break;
      case "systemUserLeft":
        systemMessage =
          chatMessage.author?.id === sessionUser.id
            ? t("page.chat-detail.chat-user-left.self")
            : t("page.chat-detail.chat-user-left.other", { author: chatMessage.author?.fullName || "" });
        break;
      case "systemUserAdded":
        if (chatMessage.users?.map((x) => x.id).includes(sessionUser.id)) {
          if (chatMessage.users.length > 1) {
            systemMessage = t("page.chat-detail.chat-user-added.you_and_others", {
              author: chatMessage.author?.fullName || "",
              count: chatMessage.users.length - 1,
            });
          } else {
            systemMessage = t("page.chat-detail.chat-user-added.you", {
              author: chatMessage.author?.fullName || "",
            });
          }
        } else if (chatMessage.users && chatMessage.users.length > 1) {
          systemMessage = t("page.chat-detail.chat-user-added.user_and_others", {
            author: chatMessage.author?.fullName || "",
            user: chatMessage.users[0].fullName,
            count: chatMessage.users.length - 1,
          });
        } else {
          systemMessage = t("page.chat-detail.chat-user-added.user", {
            author: chatMessage.author?.fullName || "",
            user: chatMessage.users?.[0]?.fullName || "",
          });
        }
        break;
      case "systemUserRemoved":
        systemMessage =
          chatMessage.author?.id === sessionUser.id
            ? t("page.chat-detail.chat-user-removed.self")
            : t("page.chat-detail.chat-user-removed.other", { author: chatMessage.author?.fullName || "" });
        break;
      case "systemGroupNameChanged":
        systemMessage =
          chatMessage.author?.id === sessionUser.id
            ? t("page.chat-detail.chat-name-changed.self")
            : t("page.chat-detail.chat-name-changed.other", { author: chatMessage.author?.fullName || "" });
        break;
      case "systemGroupImageChanged":
        systemMessage =
          chatMessage.author?.id === sessionUser.id
            ? t("page.chat-detail.chat-image-changed.self")
            : t("page.chat-detail.chat-image-changed.other", { author: chatMessage.author?.fullName || "" });
        break;
    }

    return systemMessage;
  }

  return (
    <span
      className={twJoin(
        "w-fit text-caption",
        presentation === "bubble" && "rounded-lg bg-background-secondary p-space-sm",
        presentation === "preview" && "w-full truncate text-text-secondary",
      )}
    >
      {getSystemMessage()}
    </span>
  );
}
